import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import SwipeableViews from "react-swipeable-views";
import { autoPlay } from "react-swipeable-views-utils";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { enqueueNotification } from "../../../redux/slice/notification/notificationSlice";
import { BannerConfig } from "../../../types/banner-config";
import { getTenantName } from "../../../util";
import TenantConfig, { isSharedMode, getRuntimeBanners } from "../../../util/tenantConfig";
import { cn } from "../../../util/cn";
import DefaultBanner, {
  DefaultBannerService,
  pickDefaultBannerServices,
} from "./DefaultBanner";

// Enable autoplay utility (will pause on user interaction automatically)
const AutoPlaySwipeableViews = autoPlay(SwipeableViews);

const Banners = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [banners, setBanners] = useState<BannerConfig[]>([]);
  // Use cases whose kolula-provided default banner is shown because the tenant
  // has no banner of its own. Never populated at the same time as `banners` —
  // one own banner suppresses all defaults.
  const [defaultServices, setDefaultServices] = useState<DefaultBannerService[]>(
    []
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlayEnabled, setAutoPlayEnabled] = useState(true);
  const interactionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tenantName = getTenantName();

  useEffect(() => {
    const loadImages = async () => {
      // Shared deployment: DB-driven branding banners first; a tenant that has
      // uploaded none gets the rendered default banners (one per enabled use
      // case) instead. Until 2026-08 the fallback was neuland's bundled
      // carousel — a foreign tenant's banner, and a broken image when it failed
      // to load. See docs/architecture/end-user-banner-types.md.
      if (isSharedMode()) {
        const config = await TenantConfig();
        const uploaded = getRuntimeBanners();
        // Items carry a per-language `images` map — pick the image for the
        // app's current language (base before any "-"), falling back to de →
        // en → first available. Map to BannerConfig so they become clickable +
        // voucher-applying. A bare string or a legacy single-`src` object is
        // treated as one image shown for all languages.
        const lang = i18n.language.split("-")[0];
        const own = uploaded
          .map((item) => {
            if (typeof item === "string") return { src: item };
            const images = item.images ?? {};
            const src =
              images[lang] ??
              images.de ??
              images.en ??
              Object.values(images)[0] ??
              item.src ??
              "";
            return {
              src,
              ...(item.href ? { href: item.href } : {}),
              ...(item.voucherCode ? { voucherCode: item.voucherCode } : {}),
              ...(item.type ? { type: item.type } : {}),
            };
          })
          // A stored banner that resolves to no image would render as a broken
          // frame; treat it as absent so the default banner takes over.
          .filter(({ src }) => src !== "");
        if (own.length > 0) {
          setBanners(own);
          setDefaultServices([]);
          return;
        }
        setBanners([]);
        setDefaultServices(pickDefaultBannerServices(config.services));
        return;
      }
      try {
        const module = await import(
          `../../../tenants/${tenantName}/assets/img/carouselImages/${i18n.language}/index.ts`
        );
        setBanners(module.default);
        setDefaultServices([]);
      } catch (error) {
        dispatch(
          enqueueNotification({
            severity: "error",
            message: t("home.banner_load_error"),
          })
        );
      }
    };
    loadImages();
  }, [tenantName, i18n.language, dispatch, t]);

  const handleChangeIndex = (index: number) => {
    setCurrentIndex(index);
  };

  // Pause autoplay on manual interaction for a short period, then resume
  const handleUserInteraction = useCallback(() => {
    if (!autoPlayEnabled) return;
    setAutoPlayEnabled(false);
    if (interactionTimeout.current) clearTimeout(interactionTimeout.current);
    interactionTimeout.current = setTimeout(
      () => setAutoPlayEnabled(true),
      6000
    );
  }, [autoPlayEnabled]);

  useEffect(() => {
    return () => {
      if (interactionTimeout.current) clearTimeout(interactionTimeout.current);
    };
  }, []);

  // The tenant's own banners, or — when it has none — the kolula default
  // banners for its use cases. One list feeds the carousel and the indicators,
  // so both cases swipe and paginate identically.
  const slides =
    banners.length > 0
      ? banners.map((banner, i) => {
          // The image is cut to the slot's 16:7, whatever was uploaded. Without
          // the ratio on the image itself a portrait photo kept its own height
          // and pushed the service buttons below the fold (PO, 2026-10-18) —
          // the admin preview (ImageSlot, aspect-[16/7], object-cover) already
          // showed this crop, so the operator sees what the customer sees.
          const image = (
            <img
              className="aspect-[16/7] object-cover w-full rounded-xl select-none"
              src={banner.src}
              alt={`Banner ${i + 1}`}
              draggable={false}
              onDragStart={(e) => e.preventDefault()}
            />
          );
          // Notice banners are purely informational — render the image only,
          // with no Link wrapper and no voucher deep-link. explainer/promo stay
          // clickable (and promo appends the voucher) exactly as before.
          if (banner.type === "notice") {
            return (
              <div key={i} className="w-full">
                {image}
              </div>
            );
          }
          // Guard on href first: a voucher without a target would otherwise
          // build "undefined?voucher=…". No href ⇒ non-navigating "#".
          const to = banner.href
            ? banner.voucherCode
              ? `${banner.href}?voucher=${banner.voucherCode}`
              : banner.href
            : "#";
          return (
            <div key={i} className="w-full">
              <Link
                to={to}
                onClick={handleUserInteraction}
                className="block w-full"
              >
                {image}
              </Link>
            </div>
          );
        })
      : defaultServices.map((service) => (
          <div key={service} className="w-full">
            <DefaultBanner service={service} onClick={handleUserInteraction} />
          </div>
        ));

  if (slides.length === 0) return <></>;

  return (
    <div className="mx-0 mb-9 w-full aspect-[16/7] !px-0">
      <AutoPlaySwipeableViews
        index={currentIndex}
        onChangeIndex={handleChangeIndex}
        interval={5000}
        autoplay={autoPlayEnabled && slides.length > 1}
        onSwitching={() => handleUserInteraction()}
        resistance
      >
        {slides}
      </AutoPlaySwipeableViews>
      {/* Custom Indicator */}
      {slides.length > 1 && (
        <div className="flex justify-center my-2.5 gap-1 mx-auto rounded-md">
          {slides.map((_, index) => (
            <button
              key={`indicator-${index}`}
              type="button"
              className={cn(
                "w-7 h-1 rounded-lg border-0 p-0 cursor-pointer transition-colors",
                index === currentIndex ? "bg-foreground/70" : "bg-foreground/10"
              )}
              onClick={() => {
                handleUserInteraction();
                setCurrentIndex(index);
              }}
              aria-label={`Go to banner ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Banners;
